<template>
  <div class="console-page">
    <div class="console-page__header">
      <div>
        <h1 class="console-page__title">Analytics</h1>
        <p class="console-page__placeholder">
          Track recent run outcomes, guidance gaps, and evidence coverage across the console.
        </p>
      </div>

      <div class="console-page__actions">
        <button type="button" class="button button--ghost" :disabled="loading" @click="loadData({ showLoading: true })">
          Refresh
        </button>
      </div>
    </div>

    <section class="console-panel">
      <div class="console-filters analytics-filters">
        <label class="console-filter analytics-filter--wide">
          <span class="console-filter__label">Workspace</span>
          <select v-model="workspaceFilter" class="form-input">
            <option value="">All workspaces</option>
            <option
              v-for="workspace in workspaceOptions"
              :key="workspace.workspaceId"
              :value="workspace.workspaceId"
            >
              {{ workspace.name }}
            </option>
          </select>
        </label>

        <label class="console-filter">
          <span class="console-filter__label">Recent Runs</span>
          <select v-model.number="recentJobLimit" class="form-input">
            <option :value="20">20</option>
            <option :value="50">50</option>
            <option :value="100">100</option>
          </select>
        </label>
      </div>

      <div v-if="workspaceWarning && !error" class="alert alert--error">
        {{ workspaceWarning }}
      </div>

      <ConsoleListState
        :loading="loading"
        :error="error"
        :is-empty="isEmpty"
        loading-message="Loading analytics..."
        :empty-message="emptyMessage"
      >
        <div class="console-results">
          <p class="console-results__count">
            Generated {{ formatRelativeTime(analytics.generated_at) }}
            <span v-if="analytics.scope.workspace_id">
              for {{ getWorkspaceLabel(analytics.scope.workspace_id) }}
            </span>
          </p>
        </div>

        <div class="console-signal-strip analytics-summary-strip">
          <div class="console-signal console-signal--active">
            <span class="console-signal__label">Matching Runs</span>
            <span class="console-signal__value">{{ analytics.scope.matching_jobs }}</span>
            <span class="console-signal__note">Recent runs included in the current analytics window.</span>
          </div>
          <div class="console-signal" :class="analytics.scope.jobs_with_analytics > 0 ? 'console-signal--ready' : 'console-signal--pending'">
            <span class="console-signal__label">Runs With Analytics</span>
            <span class="console-signal__value">{{ analytics.scope.jobs_with_analytics }}</span>
            <span class="console-signal__note">Runs carrying stored analytics summaries.</span>
          </div>
          <div class="console-signal" :class="analytics.guidance_gaps.jobs_with_gaps > 0 ? 'console-signal--attention' : 'console-signal--ready'">
            <span class="console-signal__label">Runs With Gaps</span>
            <span class="console-signal__value">{{ analytics.guidance_gaps.jobs_with_gaps }}</span>
            <span class="console-signal__note">Recent runs that reported guidance gaps.</span>
          </div>
          <div class="console-signal" :class="analytics.mismatch_hotspots.total_mismatches > 0 ? 'console-signal--failed' : 'console-signal--ready'">
            <span class="console-signal__label">Mismatches</span>
            <span class="console-signal__value">{{ analytics.mismatch_hotspots.total_mismatches }}</span>
            <span class="console-signal__note">Recorded mismatch hotspots in the current window.</span>
          </div>
        </div>

        <details class="console-module" open>
          <summary class="console-module__summary">
            Scope
            <span class="console-module__count">{{ analytics.scope.recent_job_limit }}</span>
          </summary>
          <dl class="console-metric-list">
            <div v-for="item in scopeMetrics" :key="item.label" class="console-metric">
              <dt>{{ item.label }}</dt>
              <dd>{{ item.value }}</dd>
            </div>
          </dl>
        </details>

        <details class="console-module" open>
          <summary class="console-module__summary">
            Outcomes
            <span class="console-module__count">{{ analytics.scope.matching_jobs }}</span>
          </summary>
          <dl class="console-metric-list">
            <div v-for="item in outcomeMetrics" :key="item.label" class="console-metric">
              <dt>{{ item.label }}</dt>
              <dd>{{ item.value }}</dd>
            </div>
          </dl>
        </details>

        <details class="console-module">
          <summary class="console-module__summary">Acceptance Closure</summary>
          <dl class="console-metric-list">
            <div v-for="item in acceptanceMetrics" :key="item.label" class="console-metric">
              <dt>{{ item.label }}</dt>
              <dd>{{ item.value }}</dd>
            </div>
          </dl>
        </details>

        <details class="console-module">
          <summary class="console-module__summary">Evidence Coverage</summary>
          <dl class="console-metric-list">
            <div v-for="item in evidenceMetrics" :key="item.label" class="console-metric">
              <dt>{{ item.label }}</dt>
              <dd>{{ item.value }}</dd>
            </div>
          </dl>
        </details>

        <details class="console-module" open>
          <summary class="console-module__summary">
            Guidance Gaps
            <span class="console-module__count">{{ analytics.guidance_gaps.total_gaps }}</span>
          </summary>
          <dl class="console-metric-list">
            <div v-for="item in guidanceMetrics" :key="item.label" class="console-metric">
              <dt>{{ item.label }}</dt>
              <dd>{{ item.value }}</dd>
            </div>
          </dl>

          <div v-if="guidanceGapTypeMetrics.length" class="analytics-gap-tags">
            <span
              v-for="item in guidanceGapTypeMetrics"
              :key="item.label"
              class="console-badge console-badge--neutral"
            >
              {{ item.label }}: {{ item.value }}
            </span>
          </div>
        </details>

        <details class="console-module">
          <summary class="console-module__summary">Mismatch Hotspots</summary>
          <dl class="console-metric-list">
            <div v-for="item in mismatchMetrics" :key="item.label" class="console-metric">
              <dt>{{ item.label }}</dt>
              <dd>{{ item.value }}</dd>
            </div>
          </dl>
        </details>

        <details class="console-module" :open="recurringTopics.length > 0">
          <summary class="console-module__summary">
            Recurring Gap Topics
            <span class="console-module__count">{{ recurringTopics.length }}</span>
          </summary>

          <div v-if="recurringTopics.length" class="console-stream">
            <article
              v-for="topic in recurringTopics"
              :key="topic.topic"
              class="console-stream__item"
            >
              <div>
                <h2 class="console-stream__title">{{ topic.topic }}</h2>
                <p class="console-stream__meta">
                  Seen in {{ topic.count }} {{ topic.count === 1 ? 'run' : 'runs' }}
                </p>
              </div>

              <div class="console-stream__aside">
                <span
                  v-for="gapType in topic.gap_types"
                  :key="gapType"
                  class="console-badge console-badge--neutral"
                >
                  {{ formatMetricLabel(gapType) }}
                </span>
              </div>
            </article>
          </div>

          <div v-else class="console-state">
            <p class="console-page__placeholder">No recurring guidance gaps in this recent window.</p>
          </div>
        </details>
      </ConsoleListState>
    </section>
  </div>
</template>

<script setup>
import { computed, onMounted, ref, watch } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import ConsoleListState from '@/components/ConsoleListState.vue'
import { formatRelativeTime } from '@/composables/useRelativeTime.js'
import { useOrchestratorRequest } from '@/composables/useOrchestratorRequest.js'
import { useFrontendLogger } from '@/services/FrontendLogger'
import { fetchWorkspaceOptions } from '@/services/workspaceOptions.js'

const route = useRoute()
const router = useRouter()
const { requestJson } = useOrchestratorRequest()
const logger = useFrontendLogger().child('[ConsoleAnalytics]')

const loading = ref(true)
const error = ref('')
const workspaceWarning = ref('')
const analytics = ref(null)
const workspaces = ref([])
const workspaceFilter = ref('')
const recentJobLimit = ref(20)
const readyForRefresh = ref(false)

const workspaceOptions = computed(() => {
  return [...workspaces.value].sort((a, b) => a.name.localeCompare(b.name))
})

const isEmpty = computed(() => {
  return !analytics.value || (analytics.value.scope?.matching_jobs ?? 0) === 0
})

const emptyMessage = computed(() => {
  if (workspaceFilter.value) {
    return 'No analytics are available for the selected workspace.'
  }
  return 'No analytics are available yet.'
})

const recurringTopics = computed(() => analytics.value?.guidance_gaps?.recurring_topics ?? [])

const scopeMetrics = computed(() => {
  const scope = analytics.value?.scope
  if (!scope) return []

  return [
    { label: 'Matching runs', value: scope.matching_jobs },
    { label: 'Runs considered', value: scope.jobs_considered },
    { label: 'Runs with analytics', value: scope.jobs_with_analytics },
    { label: 'Recent run limit', value: scope.recent_job_limit },
  ]
})

const outcomeMetrics = computed(() => toMetricEntries(analytics.value?.outcome_counts))
const acceptanceMetrics = computed(() => toMetricEntries(analytics.value?.acceptance_closure_counts))

const evidenceMetrics = computed(() => {
  const evidence = analytics.value?.evidence_coverage
  if (!evidence) return []

  return [
    { label: 'Runs with planner evidence', value: evidence.jobs_with_planner_evidence },
    { label: 'Runs with runtime evidence', value: evidence.jobs_with_runtime_evidence },
    { label: 'Planner evidence records', value: evidence.planner_evidence_records },
    { label: 'Runtime evidence records', value: evidence.runtime_evidence_records },
  ]
})

const guidanceMetrics = computed(() => {
  const gaps = analytics.value?.guidance_gaps
  if (!gaps) return []

  return [
    { label: 'Runs with gaps', value: gaps.jobs_with_gaps },
    { label: 'Total gaps', value: gaps.total_gaps },
    { label: 'Recurring topics', value: recurringTopics.value.length },
  ]
})

const guidanceGapTypeMetrics = computed(() => {
  const entries = toMetricEntries(analytics.value?.guidance_gaps?.by_type)
  return entries.filter((item) => item.value > 0)
})

const mismatchMetrics = computed(() => {
  const mismatch = analytics.value?.mismatch_hotspots
  if (!mismatch) return []

  return [
    { label: 'Runs with mismatches', value: mismatch.jobs_with_mismatches },
    { label: 'Total mismatches', value: mismatch.total_mismatches },
  ]
})

function normalizeWorkspace(raw) {
  const workspaceId = raw.id ?? raw.workspaceId
  return {
    workspaceId,
    name: raw.name || workspaceId,
  }
}

function toMetricEntries(record) {
  if (!record || typeof record !== 'object') return []

  return Object.entries(record).map(([key, value]) => ({
    label: formatMetricLabel(key),
    value,
  }))
}

function formatMetricLabel(value) {
  if (!value) return ''

  return value
    .replace(/_/g, ' ')
    .replace(/\b\w/g, (char) => char.toUpperCase())
}

function getWorkspaceLabel(workspaceId) {
  const match = workspaces.value.find((workspace) => workspace.workspaceId === workspaceId)
  return match?.name || workspaceId
}

async function fetchAnalytics() {
  const params = new URLSearchParams({
    limit: String(recentJobLimit.value),
  })

  if (workspaceFilter.value) {
    params.set('workspaceId', workspaceFilter.value)
  }

  const data = await requestJson(`/jobs/analytics?${params}`)
  analytics.value = data.analytics ?? null
}

async function fetchWorkspaces() {
  const rawWorkspaces = await fetchWorkspaceOptions(requestJson, { includeArchived: true })
  workspaces.value = rawWorkspaces.map(normalizeWorkspace)
}

async function loadData({ showLoading = false } = {}) {
  if (showLoading) loading.value = true
  error.value = ''
  workspaceWarning.value = ''

  const [analyticsResult, workspacesResult] = await Promise.allSettled([
    fetchAnalytics(),
    fetchWorkspaces(),
  ])

  if (analyticsResult.status === 'rejected') {
    analytics.value = null
    error.value = 'Failed to load analytics.'
    logger.warn('Failed to fetch analytics', analyticsResult.reason)
  }

  if (workspacesResult.status === 'rejected') {
    workspaces.value = []
    workspaceWarning.value = 'Workspace names could not be loaded. Showing workspace IDs instead.'
    logger.warn('Failed to fetch workspaces for analytics page', workspacesResult.reason)
  }

  loading.value = false
}

async function refreshAnalytics({ showLoading = false } = {}) {
  if (showLoading) loading.value = true
  error.value = ''

  try {
    await fetchAnalytics()
  } catch (fetchError) {
    analytics.value = null
    error.value = 'Failed to load analytics.'
    logger.warn('Failed to refresh analytics', fetchError)
  } finally {
    loading.value = false
  }
}

watch(
  () => route.query.workspaceId,
  (workspaceId) => {
    workspaceFilter.value = typeof workspaceId === 'string' ? workspaceId : ''
  },
  { immediate: true },
)

watch([workspaceFilter, recentJobLimit], () => {
  if (!readyForRefresh.value) {
    return
  }

  const nextQuery = {
    ...route.query,
  }

  if (workspaceFilter.value) {
    nextQuery.workspaceId = workspaceFilter.value
  } else {
    delete nextQuery.workspaceId
  }

  if (route.query.workspaceId !== nextQuery.workspaceId) {
    router.replace({ query: nextQuery })
  }

  refreshAnalytics({ showLoading: true })
})

onMounted(() => {
  loadData({ showLoading: true }).finally(() => {
    readyForRefresh.value = true
  })
})
</script>

<style scoped>
.analytics-filters {
  grid-template-columns: minmax(0, 1.5fr) minmax(180px, 0.45fr);
}

.analytics-filter--wide {
  min-width: 0;
}

.analytics-summary-strip {
  margin-bottom: 16px;
}

.analytics-gap-tags {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-top: 16px;
}

@media (max-width: 768px) {
  .analytics-filters {
    grid-template-columns: 1fr;
  }
}
</style>
